import { Map } from 'app/design/primitives/map';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Text } from 'app/design/primitives/text';
import { Column, Row } from 'app/design/primitives/box';

import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { useAllAddresses } from 'app/network/address';
import { useSelectedGroupsAndAddresses } from 'app/network/listing';
import type { PublicLocation } from 'app/types/location';
import { getDistanceBetweenCoords } from 'app/utils/helpers';
import { useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronLeft, IconChevronRight, IconInfoCircle, IconMapPin } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface ApproximateLocationMapProps {
  actionLabel?: string;
  location?: PublicLocation;
  locations?: PublicLocation[];
  title: string;
  hint: string;
  privacyMessage: string;
  compact?: boolean;
}

export function ApproximateLocationMap({
  actionLabel,
  location,
  locations,
  title,
  hint,
  privacyMessage,
  compact = false,
}: ApproximateLocationMapProps) {
  const { formatDistanceWithoutText } = useDistanceUnit();
  const { data: addresses } = useAllAddresses();
  const { selectedAddresses } = useSelectedGroupsAndAddresses();
  const selectedAddressIds = new Set(selectedAddresses);
  const unsortedPublicLocations = (() => {
    return locations?.length ? locations : location ? [location] : [];
  })();
  const referenceAddresses = (() => {
    if (!addresses?.length) {
      return [];
    }

    const filteredAddresses = selectedAddresses.length
      ? addresses.filter((address) => selectedAddressIds.has(address.id))
      : [];

    return filteredAddresses.length ? filteredAddresses : addresses.slice(0, 1);
  })();
  const publicLocations = (() => {
    if (unsortedPublicLocations.length <= 1 || !referenceAddresses.length) {
      return unsortedPublicLocations;
    }

    const closestDistanceToUser = (publicLocation: PublicLocation) =>
      referenceAddresses
        .map((address) =>
          getDistanceBetweenCoords(
            publicLocation.latitude,
            publicLocation.longitude,
            address.latitude,
            address.longitude,
          ),
        )
        .reduce((closestDistance, distance) => Math.min(closestDistance, distance), Number.POSITIVE_INFINITY);

    return [...unsortedPublicLocations].sort(
      (leftLocation, rightLocation) => closestDistanceToUser(leftLocation) - closestDistanceToUser(rightLocation),
    );
  })();

  const keyedPublicLocations = (() => {
    const keyCounts = new globalThis.Map<string, number>();
    return publicLocations.map((publicLocation) => {
      const publicLocationKey = getPublicLocationKey(publicLocation);
      const nextCount = (keyCounts.get(publicLocationKey) ?? 0) + 1;
      keyCounts.set(publicLocationKey, nextCount);
      return {
        location: publicLocation,
        key: `${publicLocationKey}-${String(nextCount)}`,
      };
    });
  })();
  const locationsKey = keyedPublicLocations.map(({ key }) => key).join('|');
  const [selection, setSelection] = useState({ locationsKey, index: 0 });
  if (selection.locationsKey !== locationsKey) {
    setSelection({ locationsKey, index: 0 });
  }
  const selectedIndex = selection.locationsKey === locationsKey ? selection.index : 0;
  const selectedLocation = publicLocations[selectedIndex] ?? publicLocations[0];

  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);

  const selectedLocationDistance = (() => {
    if (!selectedLocation || !referenceAddresses.length) {
      return undefined;
    }

    return referenceAddresses
      .map((address) =>
        getDistanceBetweenCoords(
          selectedLocation.latitude,
          selectedLocation.longitude,
          address.latitude,
          address.longitude,
        ),
      )
      .reduce((closestDistance, distance) => Math.min(closestDistance, distance), Number.POSITIVE_INFINITY);
  })();

  if (!selectedLocation) {
    return null;
  }

  const latitudeDelta = selectedLocation.radius * 0.00003;
  const longitudeDelta = selectedLocation.radius * 0.00003;
  const hasMultipleLocations = publicLocations.length > 1;
  const previousLocation = () =>
    setSelection((current) => ({
      locationsKey,
      index: current.index === 0 ? publicLocations.length - 1 : current.index - 1,
    }));
  const nextLocation = () =>
    setSelection((current) => ({ locationsKey, index: (current.index + 1) % publicLocations.length }));

  const map = (interactive: boolean, height: number) => (
    <View
      pointerEvents={interactive ? 'auto' : 'none'}
      style={{
        backgroundColor: theme.colors['surface.disabled'],
        borderColor: theme.colors['border.secondary'],
        borderRadius: theme.borderRadii['radius.lg'],
        borderWidth: 1,
        height,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Map
        key={`${interactive ? 'interactive' : 'preview'}-${String(selectedIndex)}-${getPublicLocationKey(selectedLocation)}`}
        {...(Platform.OS === 'ios' && { mapType: 'mutedStandard' })}
        height="100%"
        loadingBackgroundColor={theme.colors['surface.disabled']}
        controlledCamera={false}
        region={{
          latitude: selectedLocation.latitude,
          longitude: selectedLocation.longitude,
          latitudeDelta,
          longitudeDelta,
        }}
        recenterButton={interactive}
        scrollEnabled={interactive}
        zoomEnabled={interactive}
        loadingEnabled
        rotateEnabled={false}
        pitchEnabled={false}
        zoomControlEnabled={false}
        showsIndoors={false}
        showsMyLocationButton={false}
        width="100%"
        borderRadius="radius.lg"
        style={{ backgroundColor: theme.colors['surface.disabled'] }}
      >
        {keyedPublicLocations.map(({ key, location: publicLocation }) => (
          <Map.Circle center={publicLocation} key={key} radius={publicLocation.radius} />
        ))}
      </Map>
    </View>
  );

  return (
    <Column gap="spacing.general.x1">
      <Row alignItems="center" gap="spacing.general.x1">
        <Text style={styles.sectionTitle} variant="bodyBaseMedium">
          {title}
        </Text>
        <Pressable
          accessibilityHint={privacyMessage}
          accessibilityLabel={hint}
          accessibilityRole="button"
          hitSlop={10}
          onPress={() => setIsInfoOpen(true)}
        >
          <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.placeholder']} />
        </Pressable>
      </Row>
      {compact ? (
        <Pressable
          accessibilityHint={hint}
          accessibilityLabel={title}
          accessibilityRole="button"
          onPress={() => setIsMapOpen(true)}
        >
          {map(false, 148)}
          <Row alignItems="center" gap="spacing.general.x1" style={styles.compactMapAction}>
            <Text color="text.primary" fontWeight="600" variant="bodySmall">
              {actionLabel ?? title}
            </Text>
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.primary']} />
          </Row>
        </Pressable>
      ) : (
        map(true, 180)
      )}
      {selectedLocation.label || hasMultipleLocations ? (
        <Row alignItems="center" gap="spacing.general.x2" justifyContent="space-between">
          <Row alignItems="center" flex={1} flexWrap="wrap" gap="spacing.general.x1">
            <IconMapPin strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.secondary']} />
            {selectedLocation.label ? (
              <Text color="text.primary" fontWeight="700" variant="bodySmall">
                {selectedLocation.label}
              </Text>
            ) : null}
            {selectedLocationDistance !== undefined ? (
              <View style={styles.distanceBadge}>
                <Text color="text.secondary" fontWeight="600" variant="bodySmall">
                  {formatDistanceWithoutText(selectedLocationDistance)}
                </Text>
              </View>
            ) : null}
          </Row>
          {hasMultipleLocations ? (
            <Row alignItems="center" gap="spacing.general.x1">
              <Pressable
                accessibilityLabel="Previous location"
                accessibilityRole="button"
                hitSlop={10}
                onPress={previousLocation}
              >
                <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
              </Pressable>
              <Text color="text.placeholder" fontWeight="700" variant="bodyBase">
                {selectedIndex + 1}/{publicLocations.length}
              </Text>
              <Pressable
                accessibilityLabel="Next location"
                accessibilityRole="button"
                hitSlop={10}
                onPress={nextLocation}
              >
                <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
              </Pressable>
            </Row>
          ) : null}
        </Row>
      ) : null}
      <BottomSheet
        containerStyle={{ paddingHorizontal: theme.spacing['spacing.general.x5'] }}
        onClose={() => setIsInfoOpen(false)}
        open={isInfoOpen}
      >
        <ScrollView>
          <Column gap="spacing.general.x4" style={{ paddingBottom: theme.spacing['spacing.general.x12'] }}>
            <Text textAlign="center" variant="bodyLarge">
              {title}
            </Text>
            <Text color="text.primary" variant="bodyBase">
              {hint}
            </Text>
            <Text color="text.primary" variant="bodyBase">
              {privacyMessage}
            </Text>
          </Column>
        </ScrollView>
      </BottomSheet>
      {compact ? (
        <BottomSheet
          containerStyle={{ paddingHorizontal: theme.spacing['spacing.general.x4'] }}
          onClose={() => setIsMapOpen(false)}
          open={isMapOpen}
          showIndicator
        >
          <Column gap="spacing.general.x4" style={{ paddingBottom: theme.spacing['spacing.general.x8'] }}>
            <Text fontWeight="700" textAlign="center" variant="bodyLarge">
              {title}
            </Text>
            {map(true, 360)}
            <Text color="text.secondary" variant="bodySmall">
              {privacyMessage}
            </Text>
          </Column>
        </BottomSheet>
      ) : null}
    </Column>
  );
}

function getPublicLocationKey(location: PublicLocation) {
  return [location.latitude, location.longitude, location.radius, location.label ?? ''].map(String).join('-');
}

const styles = StyleSheet.create({
  sectionTitle: {
    flexShrink: 1,
  },
  compactMapAction: {
    position: 'absolute',
    top: theme.spacing['spacing.general.x2'],
    right: theme.spacing['spacing.general.x2'],
    minHeight: 40,
    borderColor: theme.colors['border.secondary'],
    borderRadius: theme.borderRadii['radius.full'],
    borderWidth: StyleSheet.hairlineWidth,
    backgroundColor: theme.colors['surface.default'],
    paddingHorizontal: theme.spacing['spacing.general.x3'],
    paddingVertical: theme.spacing['spacing.general.x2'],
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
  },
  distanceBadge: {
    borderRadius: theme.borderRadii['radius.full'],
    backgroundColor: theme.colors['surface.default'],
    paddingHorizontal: theme.spacing['spacing.general.x2'],
    paddingVertical: theme.spacing['spacing.general.x1'],
  },
});
